import StepVersion from './VersionStep';
import { useApiService } from '@/hooks/useApiService';
import { useEffect, useState } from 'react';
import { useParams, redirect, useNavigate } from 'react-router-dom';
import { DetailedLesson, ProjectCore, VersionAccountStatus, VersionItem } from '../types';
import { TailSpin } from 'react-loader-spinner';
import { useTranslation } from 'react-i18next';
import SelectProject from './SelectProject';

type VersionLessonProps = {
    version: VersionItem;
    theme: string;
    lesson: DetailedLesson;
    lessonValidation: boolean[];
    project?: ProjectCore;
    setLessonValidation: (value: boolean[]) => void;
    setProjectChanged: (value: boolean) => void;
    setShowSuccess: (value: boolean) => void;
    lastLesson?: number;
    showSuccess: boolean;
};

function VersionLesson({
    theme,
    lesson,
    lessonValidation,
    project,
    setLessonValidation,
    setProjectChanged,
    setShowSuccess,
    lastLesson,
    showSuccess,
}: VersionLessonProps) {
    const navigate = useNavigate();
    const { getVersionAccountStatus, checkLessonStatus } = useApiService();
    const { t } = useTranslation();
    const { formationId, versionId, lessonId } = useParams();
    const [showLoader, setShowLoader] = useState(false);
    const [versionAccountStatusId, setVersionAccountStatusId] = useState<string>();
    const [selectedProject, setSelectedProject] = useState<ProjectCore | undefined>(project);
    const [clickedVerify, setClickedVerify] = useState(false);
    const isLastLesson = lastLesson?.toString() === lessonId?.toString();
    const [versionSucceeded, setVersionSucceeded] = useState(false);

    useEffect(() => {
        if (!formationId || !versionId) {
            return;
        }

        const fetchData = async () => {
            const accountStatus: VersionAccountStatus = await getVersionAccountStatus(formationId, versionId);
            setVersionAccountStatusId(accountStatus._id);
            if (accountStatus.finished) {
                setVersionSucceeded(true);
            }

            if (accountStatus.project_id) {
                redirect(`/formation/${formationId}/version/${versionId}/lesson/1`);
            }
        };
        fetchData().catch(console.error);
        setSelectedProject(project);
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project, formationId, versionId, lessonId, showSuccess]);

    return (
        <div className="flex flex-col items-center w-full">
            <div className="flex w-full flex-row items-center justify-between">
                <h1 className="text-2xl font-semibold text-text-dark break-words">{t(lesson.name ?? '')}</h1>
                <div className="ml-4 mt-0.5 rounded-full bg-orange-500/10 px-2.5">
                    <span className="text-xs font-semibold leading-6 text-orange-500">{lesson.duration} min</span>
                </div>
            </div>
            <iframe
                className="mt-4 aspect-video w-full rounded-xl border	outline-gray-100 border-solid"
                src={lesson.video_url}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Embedded youtube"
            />
            <div className="mb-16 mt-8 flex w-full flex-col items-center gap-7 lg:flex-row-reverse lg:items-start lg:gap-16">
                <div className="flex w-full flex-col rounded-lg lg:w-1/2">
                    <div className="flex flex-row items-center justify-between">
                        <h3 className="text-md font-semibold text-text-dark"> {t('school_evaluation')}</h3>
                        <div className="flex flex-row items-center gap-2">
                            {!showLoader ? (
                                <button
                                    onClick={async () => {
                                        if (!formationId || !versionId || !lessonId) {
                                            return;
                                        }

                                        setShowLoader(true);

                                        try {
                                            const check = await checkLessonStatus(formationId, versionId, lessonId);
                                            setLessonValidation(check);
                                            setClickedVerify(false);

                                            const allStepsValidated = check.every(result => result);
                                            if (isLastLesson && allStepsValidated) {
                                                setShowSuccess(true);
                                                navigate(
                                                    `/formation/${formationId}/version/${versionId}/lesson/success`
                                                );
                                            }
                                            setClickedVerify(true);
                                        } catch (error) {
                                            console.error('Verification failed:', error);
                                        } finally {
                                            setShowLoader(false);
                                        }
                                    }}
                                    className={`text-md font-semibold text-ar-${theme}`}
                                >
                                    {t('school_verify')}
                                </button>
                            ) : (
                                <TailSpin
                                    visible={true}
                                    height="20"
                                    width="20"
                                    color="#293C64"
                                    ariaLabel="tail-spin-loading"
                                    radius="1"
                                    wrapperStyle={{}}
                                    wrapperClass="pr-5"
                                />
                            )}
                        </div>
                    </div>

                    <div className="mt-3 flex flex-col divide-y divide-solid divide-[#EAECF0] rounded-md border border-[#EAECF0]">
                        {lessonValidation &&
                            lesson.steps &&
                            lessonValidation.map((result, index) => {
                                const step = lesson?.steps?.[index];

                                if (!step) {
                                    return null;
                                }
                                {
                                    return (
                                        <StepVersion
                                            theme={theme}
                                            key={step.id}
                                            step={step}
                                            result={result}
                                            verified={clickedVerify}
                                            lessonValidation={lessonValidation}
                                        />
                                    );
                                }
                            })}
                    </div>
                </div>

                <div className="w-full border-t border-[#E0E3E9] lg:hidden"></div>
                <div className="flex w-full flex-col gap-6 lg:w-1/2">
                    <div className="flex flex-col">
                        <h3 className="text-md font-semibold text-text-dark">{t('school_how_does_it_work')}</h3>
                        <p
                            className="text-md mt-2 font-medium text-text-grey"
                            dangerouslySetInnerHTML={{ __html: `${t('school_how_does_it_work_desc')}` }}
                        ></p>
                    </div>

                    <div className="flex flex-col">
                        {!versionSucceeded && lesson.project_needed && project?.properties?.name && (
                            <div className="flex flex-col">
                                <CustomSeparator />
                                <h3 className="text-md font-semibold text-text-dark mt-6 mb-3">
                                    {t('school_project_currently_tested')}{' '}
                                    <span className="text-text-grey font-medium">({t('school_editable')})</span>
                                </h3>

                                <SelectProject
                                    currentSelectedProject={selectedProject}
                                    onProjectChange={(project: ProjectCore) => {
                                        setSelectedProject(project);
                                        setProjectChanged(true);
                                    }}
                                    formationId={formationId ?? ''}
                                    versionId={versionId ?? ''}
                                    versionAccountStatusId={versionAccountStatusId ?? ''}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default VersionLesson;

function CustomSeparator() {
    return <div className="w-full border-t border-[#E0E3E9]"></div>;
}
